// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Dictionary } from "@insite/client-framework/Common/Types";
import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getAccount } from "@insite/client-framework/Services/AccountService";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { getCart, GetCartApiParameter } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { chain, NavigateToReturnUrl } from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getPageByLink } from "@plumberssupply/XDL/Utilities";

const mapReturnToPage: Dictionary<string> = {
    OrderDetailsPage: "OrderHistoryPage",
    InvoiceDetailsPage: "InvoiceHistoryPage",
    OrderApprovalDetailsPage: "OrderApprovalListPage",
};

const getReturnUrl = (state: ApplicationState, url?: string) => {
    const returnUrl = url ? decodeURIComponent(url) : undefined;
    const pageToReturn = getPageByLink(state, returnUrl);
    if (pageToReturn?.type) {
        const mappedUrl = getPageLinkByPageType(state, mapReturnToPage[pageToReturn.type])?.url;
        return mappedUrl || returnUrl;
    }
    return returnUrl;
};

// XNG-change: override to handle return url if already exists
export const XngNavigateToReturnUrl: typeof NavigateToReturnUrl = async props => {
    if (props.parameter.skipRedirect) {
        return;
    }
    // if user select 'Create New' ship to
    const state = props.getState();

    if (props.apiParameter.session.shipToId === "") {
        window.location.href = `${getPageLinkByPageType(state, "AddressesPage")?.url}?isNewShipTo=true`;
        return;
    }

    let returnUrl = getReturnUrl(state, props.parameter.returnUrl);

    // if customer is not assigned to current user, return to UserAdministration page.
    const { userId } = getQueryStrings(state);
    const userSetupPageUrl = getPageLinkByPageType(state, "UserSetupPage")?.url;
    if (
        returnUrl &&
        userSetupPageUrl &&
        returnUrl.toLowerCase().indexOf(userSetupPageUrl.toLowerCase()) > -1 &&
        userId
    ) {
        try {
            await getAccount({ accountId: userId, expand: ["approvers", "roles"] });
        } catch (error) {
            window.location.href = getPageLinkByPageType(state, "UserListPage")!.url;
            return;
        }
    }

    // redirect to default urls if they exist
    const session = getSession(state);
    const defaultUrl = session.dashboardIsHomepage
        ? getPageLinkByPageType(state, "MyAccountPage")?.url
        : session.customLandingPage;

    // XNG-Change: don't redirect to default url if return url is already set
    if ((!returnUrl || returnUrl === "/") && defaultUrl) {
        window.location.href = defaultUrl;
        return;
    }

    // check for restricted products / bypass shipping page if needed
    const checkoutShippingUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    if (returnUrl?.toLowerCase() === checkoutShippingUrl?.toLowerCase()) {
        const cartResult = await getCart({
            cartId: API_URL_CURRENT_FRAGMENT,
            expand: ["cartLines", "hiddenproducts"],
        } as GetCartApiParameter);

        const { canCheckOut, canBypassCheckoutAddress } = cartResult.cart;
        const checkoutReviewAndSubmitUrl = getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url;
        const cartUrl = getPageLinkByPageType(state, "CartPage")?.url;

        if (!canCheckOut || session.isRestrictedProductExistInCart) {
            returnUrl = cartUrl;
        } else if (canBypassCheckoutAddress) {
            returnUrl = checkoutReviewAndSubmitUrl;
        }
    }

    window.location.href = returnUrl || getHomePageUrl(state);
};

replaceInChain(chain, NavigateToReturnUrl, XngNavigateToReturnUrl);
